<template>
  <section id="appointment-questionnaire" class="px-3">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">
    </Breadcrumbs>

    <div v-if="isLoading" class="d-flex justify-content-center align-items-center flex-column py-4">
      <span class="mb-3">{{$t('clinical.people.anamnesis.loading')}}</span>
      <hollow-dots-spinner
        :animation-duration="1000"
        :size="60"
        color="#29c4a9"
      />
    </div>

    <div v-else-if="appointment && person" class="py-4">

      <section id="appointment-data" class="questionnaire-section">

        <AppointmentScheduleAndCompanion
          :appointment="appointment"
          :value="appointment"
          :service="component.service"
          :editing="false"
        />

      </section>

      <section class="questionnaire-section">
        <h6 class="is-bold">
          {{$t('clinical.people.anamnesis.clinical_questionnaire')}}
        </h6>

        <ClinicalQuestionnaireForm
          v-if="appointment.parent_id == null"
          class="ml-3"
          :value="Array.isArray(appointment.clinical_questionnaires) && appointment.clinical_questionnaires.length ? appointment.clinical_questionnaires[0] : null"
          :collapsable="true"
          :editing="false"
        />

        <LifeStyleForm
          v-if="appointment.parent_id == null"
          class="ml-3"
          :value="Array.isArray(appointment.life_styles) && appointment.life_styles.length ? appointment.life_styles[0] : null"
          :collapsable="true"
          :editing="false"
        />

        <OtoscopyForm
          class="ml-3"
          :value="Array.isArray(appointment.otoscopies) && appointment.otoscopies.length ? appointment.otoscopies[0] : null"
          :collapsable="true"
          :editing="false"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <!-- With Otoscopy -->
            <div class="row align-items-center">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_otoscopy')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  :value="appointment.with_otoscopy === null ? false : appointment.with_otoscopy"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  class="col-4 col-lg-3 px-0"
                  :disabled="true"

                />
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </OtoscopyForm>

        <ImpedanceForm
          class="ml-3"
          :value="Array.isArray(appointment.impedances) && appointment.impedances.length ? appointment.impedances[0] : null"
          :collapsable="true"
          :editing="false"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <!-- With Impedance -->
            <div class="row align-items-center">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_impedance')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  :value="appointment.with_impedance === null ? false : appointment.with_impedance"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  class="col-4 col-lg-3 px-0"
                  :disabled="true"

                />
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </ImpedanceForm>

        <TonalAudiometryForm
          class="ml-3"
          :value="Array.isArray(appointment.tonal_audiometries) && appointment.tonal_audiometries.length ? appointment.tonal_audiometries[0] : null"
          :collapsable="true"
          :editing="false"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <!-- With Tonal Audiometry -->
            <div class="row align-items-center">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_tonal_audiometry')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  :value="appointment.with_tonal_audiometry === null ? false : appointment.with_tonal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  class="col-4 col-lg-3 px-0"
                  :disabled="true"
                />
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>

        </TonalAudiometryForm>

        <VocalAudiometryForm
          class="ml-3"
          :value="Array.isArray(appointment.vocal_audiometries) && appointment.vocal_audiometries.length ? appointment.vocal_audiometries[0] : null"
          :collapsable="true"
          :editing="false"
        >
          <div slot="beforeForm" class="evollu-form mb-3">
            <!-- With Tonal Audiometry -->
            <div class="row align-items-center">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.with_vocal_audiometry')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  :value="appointment.with_vocal_audiometry === null ? false : appointment.with_vocal_audiometry"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  class="col-4 col-lg-3 px-0"
                  :disabled="true"
                />
              </div>
            </div>
          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>
        </VocalAudiometryForm>

        <HearingDevicesForm
          class="ml-3"
          :value="Array.isArray(appointment.hearing_devices) && appointment.hearing_devices.length ? appointment.hearing_devices : null"
          :collapsable="true"
          :editing="false"
        >

          <div slot="beforeForm" class="evollu-form mb-3">

            <!-- With Hearing Device Sale -->
            <div class="row align-items-center">
              <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
                {{$t('clinical.people.anamnesis.sale')}}
              </label>

              <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
                <va-radio-button
                  v-for="(option, index) in booleanOptions"
                  :value="appointment.with_sale === null ? false : appointment.with_sale"
                  :key="index"
                  :option="option.value"
                  :label="$t(option.name)"
                  class="col-4 col-lg-3 px-0"
                  :disabled="true"
                />
              </div>
            </div>

          </div>

          <template slot="noData">
            <span class="d-none">Hide the Default no data message</span>
          </template>
        </HearingDevicesForm>

        <ObservationsForm
          class="ml-3"
          :value="appointment.anamnesis_obs"
          :collapsable="false"
          :editing="false"
        />
      </section>

    </div>

    <div v-else class="d-flex align-items-center flex-column py-5">
      <span class="mb-3">{{$t('clinical.people.anamnesis.loading_failed')}}</span>
    </div>

  </section>
</template>

<script>

import { booleanOptions } from '@/components/clinical/people/Anamnesis/AnamnesisOptions'
import { HollowDotsSpinner } from 'epic-spinners'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { AppointmentService, PersonService } from '@/api/AuthClinicalService'
import PersonForm from '@/components/clinical/people//PersonForm'
import ClinicalQuestionnaireForm from '@/components/clinical/people/Anamnesis/ClinicalQuestionnaireForm'
import LifeStyleForm from '@/components/clinical/people/Anamnesis/LifeStyleForm'
import OtoscopyForm from '@/components/clinical/people/Anamnesis/OtoscopyForm'
import TonalAudiometryForm from '@/components/clinical/people/Anamnesis/TonalAudiometryForm'
import VocalAudiometryForm from '@/components/clinical/people/Anamnesis/VocalAudiometryForm'
import HearingDevicesForm from '@/components/clinical/people/Anamnesis/HearingDevicesForm'
import ObservationsForm from '@/components/clinical/people/Anamnesis/ObservationsForm'
import AppointmentScheduleAndCompanion from '@/components/clinical/appointments/AppointmentScheduleAndCompanion'
import Errors from '@/utils/StandardErrors'
import ImpedanceForm from "@/components/clinical/people/Anamnesis/ImpedanceForm";

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    ImpedanceForm,
    Breadcrumbs,
    HollowDotsSpinner,
    PersonForm,
    ClinicalQuestionnaireForm,
    LifeStyleForm,
    OtoscopyForm,
    TonalAudiometryForm,
    VocalAudiometryForm,
    HearingDevicesForm,
    ObservationsForm,
    AppointmentScheduleAndCompanion,
  },
  computed: {
    personName: function () {
      if (!this.person) {
        return null
      }
      return this.person.last_name ? `${this.person.first_name} ${this.person.last_name}` : this.person.first_name
    },
    breadcrumbs: function () {
      if (this.previousRoute && this.previousRoute.name && this.previousRoute.name.endsWith('people/id')) {
        return [
          { title: this.$t(`menu.${this.serviceSlug}`), disabled: true },
          { title: this.$t('menu.clinical/people'), meta: { type: 'function', value: () => this.$router.push(`/${this.serviceSlug}/clinical/people`) } },
          { title: this.personName, meta: { type: 'function', value: () => this.$router.push(`/${this.serviceSlug}/clinical/people/${this.person.id}`) } },
          { title: `${this.$t('clinical.appointments.appointment_made_at')} ${this.appointment.created_at.substring(0, 10)}` }
        ]
      }
      return [
        { title: this.$t('menu.clinical/appointments'), meta: { type: 'string', value: `/${this.serviceSlug}/clinical/appointments` } },
        { title: this.personName, active: true }
      ]
    }
  },
  data: function () {
    return {
      isLoading: true,
      booleanOptions,
      appointment: null,
      person: null,
      personForm: {
        data: null,
        errors: new Errors()
      },
      previousRoute: null
    }
  },
  methods: {
    getAppointment: function (appointmentId) {
      return new Promise((resolve, reject) => {
        AppointmentService.get(this.component.service.url, appointmentId)
          .then(appointment => {
            this.setAppointment(appointment)
            resolve(appointment)
          })
          .catch(error => reject(error))
      })
    },
    getPerson: function (personId) {
      return new Promise((resolve, reject) => {
        PersonService.get(this.component.service.url, personId)
          .then(person => {
            this.setPerson(person)
            resolve(person)
          })
          .catch(error => reject(error))
      })
    },
    setPerson: function (person) {
      this.person = person
    },
    setAppointment: function (appointment) {
      this.appointment = appointment
    },
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.getAppointment(to.params.id)
        .then(appointment => {
          if (appointment.started_at !== null) {
            vm.getPerson(appointment.person.id)
              .finally(() => {
                vm.isLoading = false
                vm.previousRoute = from
              })
          } else {
            vm.isLoading = false
            vm.$router.go(-1)
          }
        })
        .catch(error => {
          console.log(error)
          vm.isLoading = false
          switch (error.response.status) {
          case 403:
            vm.showToast(vm.$t('clinical.appointments.missing_permissions'), vm.$store.state.errorToastOptions)
            vm.$router.go(-1)
            break
          case 404:
            vm.$router.go(-1)
            break
          default:
          }
        })
    })
  },
  beforeRouteUpdate (to, from, next) {
    this.isLoading = true
    this.getAppointment(to.params.id)
      .then(appointment => {
        if (appointment.started_at !== null) {
          this.getPerson(appointment.person.id)
            .finally(() => {
              this.isLoading = false
              this.previousRoute = from
            })
        } else {
          this.isLoading = false
          this.$router.go(-1)
        }
      })
      .catch(error => {
        console.log(error)
        this.isLoading = false
        switch (error.response.status) {
        case 403:
          this.showToast(this.$t('clinical.appointments.missing_permissions'), this.$store.state.errorToastOptions)
          this.$router.go(-1)
          break
        case 404:
          this.$router.go(-1)
          break
        default:
        }
      })
  }
}
</script>

<style lang="scss">

#appointment-questionnaire {

  .questionnaire-section {
    margin-bottom: 3rem;
  }

  .actions-wrapper {

    .va-button {
      min-width: 8rem;
    }
  }
}
</style>
